import clsx from "clsx"
import { FC, useMemo } from "react"
import { Trans, useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { GameDogLoseImg, GameDogWinImg, GoldenDustImg } from "@/assets"
import { CurrencyIcon } from "@/helpers/currencyIcon"
import { ROUTES } from "@/helpers/routes"
import { useModal } from "@/modals"
import { useAppSelector } from "@/store"
import { useGetPlayerProfileQuery } from "@/store/apis/playerAPI"
import { GameplaySelectors } from "@/store/slices/gameplay"
import { MatchSelectors } from "@/store/slices/match"
import { Button } from "@/ui/Button"
import { Modal } from "@/ui/Modal"
import { OutlinedText } from "@/ui/OutlinedText"
import styles from "./FinishMatchModal.module.scss"

const modalType = "finishMatch"

export const FinishMatchModal: FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { modal } = useModal(modalType)

  const matchSession = useAppSelector(MatchSelectors.selectMatchSession())
  const isWinner = useAppSelector(GameplaySelectors.selectIsWinner())
  const isTonMatch = useAppSelector(GameplaySelectors.selectIsTonMatch())

  const { data: playerProfile } = useGetPlayerProfileQuery()

  const i18KeyTypePrefix = isTonMatch ? "ton" : "common"
  const i18KeyVictoryPrefix = isWinner ? "victory" : "defeat"

  const goldenDustReward =
    matchSession?.reward?.[playerProfile?._id ?? ""]?.goldenDust ?? 0

  const { title, text, icon } = useMemo(() => {
    return {
      title: t(
        `finishMatchModal.${i18KeyTypePrefix}.${i18KeyVictoryPrefix}.title`
      ),
      text: (
        <Trans
          i18nKey={`finishMatchModal.${i18KeyTypePrefix}.${i18KeyVictoryPrefix}.text`}
          values={{
            count: matchSession?.pot?.prizeValue || 0
          }}
          components={{
            ton: <CurrencyIcon.ton />
          }}
        />
      ),
      icon: i18KeyVictoryPrefix === "victory" ? GameDogWinImg : GameDogLoseImg
    }
  }, [i18KeyTypePrefix, i18KeyVictoryPrefix])

  const closeModal = () => {
    modal.close()
  }

  const handleToHome = () => {
    closeModal()
    navigate(ROUTES.HOME)
  }

  return (
    <Modal.Center
      type={modalType}
      className={clsx(styles.FinishMatchModal, {
        [styles.winner]: isWinner,
        [styles.ton]: isTonMatch,
        [styles.goldenDust]: goldenDustReward > 0
      })}
      body={{ className: styles.FinishMatchModal_body }}
      onClose={closeModal}
    >
      <Modal.StoneFlexContainer
        styles={isTonMatch ? "ton" : undefined}
        content={{ className: styles.FinishMatchModal_content }}
        outside={
          <>
            <div className={clsx(styles.FinishMatchModal_buttons)}>
              <Button
                className={clsx(styles.FinishMatchModal_button, styles.home)}
                styles="orange-3d"
                onClick={handleToHome}
              >
                {t("button.toHome")}
              </Button>
              <Button
                className={clsx(
                  styles.FinishMatchModal_button,
                  styles.continue
                )}
                styles="green"
                onClick={closeModal}
              >
                {t("button.continue")}
              </Button>
            </div>
          </>
        }
      >
        <>
          <div className={clsx(styles.FinishMatchModal_dog)}>
            <img src={icon} alt="dog" />
          </div>
          <OutlinedText className={clsx(styles.FinishMatchModal_title)}>
            {title}
          </OutlinedText>
          {goldenDustReward > 0 && (
            <div className={clsx(styles.FinishMatchModal_reward)}>
              <span>+{goldenDustReward}</span>
              <img src={GoldenDustImg} alt="golden dust" />
            </div>
          )}
          <div className={clsx(styles.FinishMatchModal_text)}>{text}</div>
        </>
      </Modal.StoneFlexContainer>
    </Modal.Center>
  )
}
